import React, { useEffect, useState } from 'react';
import { Receipt, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import '../../styles/admin/Transactions.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

export default function Transactions() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    averageOrder: 0,
  });
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState({
    open: false,
    purchase: null,
    text: '',
  });

  const fetchPurchases = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/purchases`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.purchases || [];
        setPurchases(list);

        const total = list.reduce((sum, p) => sum + (p.amount_paid || 0), 0);
        setStats({
          totalRevenue: total,
          totalTransactions: list.length,
          averageOrder: list.length > 0 ? total / list.length : 0,
        });
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const openConfirm = (purchase) => {
    setConfirmState({ open: true, purchase, text: '' });
  };

  const closeConfirm = () => {
    setConfirmState({ open: false, purchase: null, text: '' });
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/purchases/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (res.ok) {
        await fetchPurchases();
        closeConfirm();
      }
    } catch (error) {
      console.error('Error deleting purchase:', error);
    }
  };

  const filteredPurchases = purchases.filter((p) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return (
      p.user_email?.toLowerCase().includes(term) ||
      p.program_name?.toLowerCase().includes(term) ||
      (p.status || '').toLowerCase().includes(term) ||
      String(p.id).includes(term)
    );
  });

  return (
    <div className="admin-transactions">
      <div className="transactions-header">
        <div>
          <h1>Transactions</h1>
          <p>View and manage all purchases.</p>
        </div>
      </div>

      {/* Search */}
      <div className="transactions-search">
        <input
          type="text"
          placeholder="Search by user, program, status, or ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card-mini">
          <div className="stat-icon bg-blue">
            <Receipt className="h-5 w-5" />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalTransactions}</span>
            <span className="stat-label">Total Transactions</span>
          </div>
        </div>
        <div className="stat-card-mini">
          <div className="stat-icon bg-green">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="stat-info">
            <span className="stat-value">₹{stats.totalRevenue.toLocaleString()}</span>
            <span className="stat-label">Total Revenue</span>
          </div>
        </div>
        <div className="stat-card-mini">
          <div className="stat-icon bg-purple">
            <TrendingDown className="h-5 w-5" />
          </div>
          <div className="stat-info">
            <span className="stat-value">₹{Math.round(stats.averageOrder)}</span>
            <span className="stat-label">Average Order</span>
          </div>
        </div>
      </div>

      <div className="transactions-table-card">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Program</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-10">Loading…</td>
              </tr>
            ) : filteredPurchases.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-10">No transactions found.</td>
              </tr>
            ) : (
              filteredPurchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td className="font-mono text-xs">#{purchase.id}</td>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar-sm">
                        {purchase.user_email ? purchase.user_email.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <span className="text-sm">{purchase.user_email || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>{purchase.program_name || 'Unknown'}</td>
                  <td className="font-medium">₹{purchase.amount_paid}</td>
                  <td>
                    <div className="date-cell">
                      <Calendar className="h-4 w-4" />
                      {new Date(purchase.purchase_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        purchase.status === 'completed'
                          ? 'badge-success'
                          : purchase.status === 'refunded'
                          ? 'badge-danger'
                          : 'badge-pending'
                      }`}
                    >
                      {purchase.status}
                    </span>
                  </td>
                  <td className="text-right">
                    <button
                      className="btn-icon text-red-500"
                      onClick={() => openConfirm(purchase)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirmState.open && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Delete transaction #{confirmState.purchase?.id}?</h3>
            <p>
              Type <strong>delete</strong> to confirm. This action cannot be undone.
            </p>
            <input
              autoFocus
              type="text"
              value={confirmState.text}
              onChange={(e) => setConfirmState({ ...confirmState, text: e.target.value })}
              placeholder="delete"
            />
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={closeConfirm}>
                Cancel
              </button>
              <button
                className="btn-danger"
                disabled={confirmState.text.trim().toLowerCase() !== 'delete'}
                onClick={() => handleDelete(confirmState.purchase?.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
